import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { supabase } from "@/integrations/supabase/client";
import { provisionResetAccount } from "@/lib/reset-account.functions";

const OPS_RESET_ENDPOINT = "https://ops.mrpanda.gr/api/public/auth/reset-password";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/reset-password" },
      { title: "Επαναφορά κωδικού — MrPanda.gr" },
      { name: "description", content: "Επανάφερε με ασφάλεια τον κωδικό πρόσβασης του λογαριασμού σου στο MrPanda.gr." },
      { name: "robots", content: "noindex, follow" },
    ] }),
  component: ResetPage,
});

function ResetPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);
  const [saving, setSaving] = useState(false);
  // Our own one-time reset token, delivered as ?reset_token=... in the email
  // link. It is NOT a Supabase recovery token — never call verifyOtp /
  // updateUser with it. The token is consumed only by the OPS endpoint when the
  // customer submits the new password.
  const [token, setToken] = useState<string | null>(null);
  const provision = useServerFn(provisionResetAccount);
  // Standard recovery link (email from auth): the client establishes a
  // recovery session from #access_token / ?code= and we update via updateUser.
  const [recovery, setRecovery] = useState(false);
  // Our own email link (?token_hash=…&type=recovery). We do NOT verify it on
  // page load — mailbox scanners open links and would burn the one-time token.
  // It is consumed only when the customer presses «Αλλαγή κωδικού».
  const [tokenHash, setTokenHash] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const t = url.searchParams.get("reset_token");
    if (t) {
      setToken(t);
      setMsg("Βάλε τον νέο κωδικό σου.");
      return;
    }
    const th = url.searchParams.get("token_hash");
    if (th) {
      setTokenHash(th);
      setMsg("Βάλε τον νέο κωδικό σου.");
      try { sessionStorage.removeItem("mp-recovery"); } catch { /* ignore */ }
      return;
    }
    let flag: string | null = null;
    try { flag = sessionStorage.getItem("mp-recovery"); } catch { /* ignore */ }
    const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
    const looksRecovery =
      flag === "1" ||
      hash.get("type") === "recovery" || url.searchParams.get("type") === "recovery" ||
      !!url.searchParams.get("code");
    const expired = () => {
      setErrored(true);
      setMsg("Ο σύνδεσμος επαναφοράς έληξε ή έχει ήδη χρησιμοποιηθεί. Ζήτησε νέο email παρακάτω.");
    };
    if (flag === "error" && !looksRecovery) {
      try { sessionStorage.removeItem("mp-recovery"); } catch { /* ignore */ }
      expired();
      return;
    }
    const enable = () => {
      setRecovery(true);
      setErrored(false);
      setMsg("Βάλε τον νέο κωδικό σου.");
    };
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") enable();
    });
    (async () => {
      const code = url.searchParams.get("code");
      if (code) {
        const { data: s } = await supabase.auth.getSession();
        if (!s.session) await supabase.auth.exchangeCodeForSession(code).catch(() => null);
      }
      if (!looksRecovery) return;
      // Η βιβλιοθήκη μπορεί να επεξεργάζεται ακόμη το #access_token — περιμένουμε λίγο.
      for (let i = 0; i < 10; i++) {
        const { data: s } = await supabase.auth.getSession();
        if (s.session) return enable();
        await new Promise((r) => setTimeout(r, 300));
      }
      try { sessionStorage.removeItem("mp-recovery"); } catch { /* ignore */ }
      expired();
    })();
    return () => sub.subscription.unsubscribe();
  }, []);
  const showPwd = !!token || recovery || !!tokenHash;

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrored(false);
    setSaving(true);
    try {
      const res = await fetch("/api/public/password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        const d = await res.json().catch(() => ({}));
        setErrored(true);
        setMsg(d?.error || "Πάρα πολλές προσπάθειες. Δοκίμασε ξανά σε λίγο.");
        return;
      }
      if (!res.ok) throw new Error("fallback");
    } catch {
      // Εφεδρική ροή αν ο δικός μας αποστολέας email δεν είναι διαθέσιμος.
      try { await provision({ data: { email } }); } catch { /* non-fatal */ }
      await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
    } finally {
      setSaving(false);
    }
    setMsg("Αν υπάρχει λογαριασμός με αυτό το email, σου στείλαμε σύνδεσμο επαναφοράς. Έλεγξε και τα «Ανεπιθύμητα» (spam).");
  };

  const setPwd = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrored(false);
    if (newPassword.length < 8) {
      setMsg("Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.");
      return;
    }
    if (newPassword.length > 72) {
      setMsg("Ο κωδικός μπορεί να έχει έως 72 χαρακτήρες.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMsg("Οι δύο κωδικοί δεν ταιριάζουν.");
      return;
    }
    setSaving(true);
    if (!token) {
      if (tokenHash) {
        const { data: s } = await supabase.auth.getSession();
        // Ίσως ήδη επαληθεύτηκε σε προηγούμενη προσπάθεια (π.χ. αδύναμος κωδικός).
        if (!s.session || !recovery) {
          const { error: vErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
          if (vErr) {
            setSaving(false);
            setErrored(true);
            setMsg("Ο σύνδεσμος επαναφοράς έληξε ή έχει ήδη χρησιμοποιηθεί. Ζήτησε νέο email.");
            return;
          }
          setRecovery(true);
        }
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (!error) {
        try { sessionStorage.removeItem("mp-recovery"); } catch { /* ignore */ }
        await supabase.auth.signOut({ scope: "local" }).catch(() => null);
        nav({ to: "/login", search: { reset: "success" } });
        return;
      }
      setSaving(false);
      setErrored(true);
      if (/same|different/i.test(error.message)) setMsg("Ο νέος κωδικός πρέπει να διαφέρει από τον παλιό.");
      else if (/weak|pwned|compromised|leaked/i.test(error.message)) setMsg("Ο κωδικός είναι πολύ αδύναμος ή έχει διαρρεύσει. Διάλεξε άλλον.");
      else if (/session|jwt|expired|missing/i.test(error.message)) setMsg("Ο σύνδεσμος επαναφοράς έληξε. Ζήτησε νέο email.");
      else setMsg("Δεν ήταν δυνατή η αλλαγή κωδικού. Δοκίμασε ξανά.");
      return;
    }
    try {
      const res = await fetch(OPS_RESET_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        nav({ to: "/login", search: { reset: "success" } });
        return;
      }
      setSaving(false);
      setErrored(true);
      setMsg(
        data?.error ||
          "Ο σύνδεσμος επαναφοράς έληξε ή έχει ήδη χρησιμοποιηθεί.",
      );
    } catch {
      setSaving(false);
      setErrored(true);
      setMsg("Σφάλμα δικτύου, ξαναπροσπάθησε.");
    }
  };

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-md px-4 py-10">
        <h1 className="mb-6 text-2xl font-bold">Επαναφορά κωδικού</h1>
        <form onSubmit={showPwd ? setPwd : requestReset} className="space-y-3 rounded-lg border border-border bg-card p-6">
          {showPwd ? (
            <>
              <label className="block text-sm">Νέος κωδικός<input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type="password" minLength={8} maxLength={72} required disabled={saving} className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 disabled:opacity-60" /></label>
              <label className="block text-sm">Επιβεβαίωση νέου κωδικού<input value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} type="password" minLength={8} maxLength={72} required disabled={saving} className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 disabled:opacity-60" /></label>
            </>
          ) : (
            <label className="block text-sm">Email<input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3" /></label>
          )}
          {msg && (
            <div className={`rounded p-2 text-sm ${errored ? "bg-destructive/10 text-destructive" : "bg-muted"}`}>{msg}</div>
          )}
          {showPwd && errored && (
            <Link to="/reset-password" reloadDocument className="block text-center text-sm text-primary hover:underline">
              Ζήτησε νέο email επαναφοράς
            </Link>
          )}
          <button type="submit" disabled={saving} className="w-full rounded-md bg-primary py-2.5 font-semibold text-primary-foreground disabled:opacity-60">{showPwd ? (saving ? "Αποθήκευση..." : "Αλλαγή κωδικού") : "Αποστολή link"}</button>
        </form>
      </div>
    </StorefrontLayout>
  );
}
